import React, { useEffect } from 'react';
import Breadcrumb from 'components/commons/Breadcrumb';
import { Switch } from 'antd';
import RestList from 'containers/rest/List';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { formatDateTime } from 'utils/formattedString';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import { Link, useNavigate } from 'react-router-dom';
import ButtonManageRoles from '../components/ButtonManageRoles';
import { newCheckRole } from 'utils/tools';
import { useDispatch, useSelector } from '@redux';
import { TLayoutItem } from '@redux/layouts/actions';
import FilterComponent from '../components/Filters/FilterComponent';
import { ACTIVE_STATUS } from 'constants/constants';
import { upperFirst } from 'lodash';
import crudActions from '@redux/crudActions';

const breadcrumbs = [
  {
    title: 'Mores',
    href: '#',
  },
  {
    title: 'Users',
  },
];

const columns: IRestColumnProps[] = [
  {
    name: 'username',
    title: 'Name',
    isDefault: true,
    render: (dt, rc: DynamicObjectType) => (
      <Link
        className="text-highlight"
        to={`/users/${rc?.id}/edit`}
      >{`${rc?.firstName} ${rc.lastName}`}</Link>
    ),
  },
  {
    name: 'email',
    title: 'Email',
    isDefault: true,
  },
  {
    name: 'role',
    title: 'Role',
    isDefault: true,
    render: (dt, rc: DynamicObjectType) => (
      <>{(rc?.roles?.map((e) => upperFirst(e.name)) || []).join(', ')}</>
    ),
  },
  {
    name: 'isActive',
    title: 'Status',
    render: (d, r, i, handleEditRow) => {
      return (
        <Switch
          onChange={(newVal) => {
            handleEditRow({ id: r.id, isActive: newVal });
          }}
          checked={!!d}
        />
      );
    },
    isDefault: true,
  },
  {
    name: 'createdAt',
    title: 'Create at',
    render: (d) => <p>{formatDateTime(d)}</p>,
    isDefault: true,
    sorter: true,
  },
];

const filters: TFilterItem[] = [
  {
    key: 'roles.id',
    label: 'Role',
    valueCPN: (
      <FilterComponent
        reference={{
          labelProp: 'name',
          model: 'roles',
          searchKey: 'role',
          initFilters: {
            join: [],
            filters: [
              {
                condition: 'and',
                operator: 'is',
                key: 'isActive',
                value: true,
              },
            ],
          },
        }}
      />
    ),
  },
  {
    key: 'isActive',
    label: 'Status',
    dataSource: ACTIVE_STATUS?.map((e: DynamicObjectType) => ({
      value: e.value,
      label: e.text,
    })),
  },
  {
    key: 'chainStore',
    label: 'Brand',
    valueCPN: (
      <FilterComponent
        reference={{
          labelProp: 'name',
          model: 'chain-stores',
          searchKey: 'name',
        }}
      />
    ),
  },
  {
    key: 'locationCode',
    label: 'Warehouse',
    valueCPN: (
      <FilterComponent
        reference={{
          labelProp: 'name',
          model: 'locations',
          searchMoreKeys: ['id', 'name'],
          operator: 'contL',
          valueProp: 'id',
          labelFormat: (item) => (
            <div>
              {item?.name ? `${item?.name} | ${item?.code}` : item?.code}
            </div>
          ),
        }}
      />
    ),
  },
];

const INITIAL_LAYOUTS: TLayoutItem[] = [];

const UsersList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector<DynamicObjectType>((state) => state.auth.data);

  const restInitialFilter: FilterFromUrlResponseType = {
    join: ['roles'],
  };

  const extraActionsFilter = newCheckRole(
    '/roles',
    'GET',
    user?.roles,
    user?.isSuperUser,
  ) && <ButtonManageRoles onClick={() => navigate('/rolePermissions')} />;

  useEffect(() => {
    dispatch(crudActions['users'].clearCurrent());
  }, [dispatch]);

  return (
    <>
      <div className="mb-[20px]">
        <Breadcrumb items={breadcrumbs} />
      </div>
      <RestList
        initialFilter={restInitialFilter}
        columns={columns}
        model="users"
        hasSearch
        filters={filters}
        hasImport={false}
        hasExport={false}
        hasFilter
        // hasOnlyFilter
        searchKeys={['lastName', 'firstName', 'email']}
        placeholderInputSearch="Search by name, email, phone"
        gotoCreatePage={() => navigate('/users/create')}
        extraActionFilters={extraActionsFilter}
        initialLayouts={INITIAL_LAYOUTS}
      />
    </>
  );
};

export default UsersList;
